/**
 * Favorites API client
 */

import { getAuthHeader } from './auth';
import { getApiUrl } from './config';

const API_BASE = '/api';

/**
 * Helper to build full API URL
 */
function favUrl(path: string): string {
  return getApiUrl(`${API_BASE}${path}`);
}

export interface FavoriteResponse {
  favorited: boolean;
  favoritesCount: number;
}

export interface FavoriteStatus {
  favoritesCount: number;
  isFavorited: boolean;
}

export interface UserFavorite {
  contractAddress: string;
  tokenId: string;
  favoritedAt: string;
  favoritesCount: number;
}

export interface UserFavoritesResponse {
  items: UserFavorite[];
  pagination: {
    offset: number;
    limit: number;
    totalCount: number;
    hasMore: boolean;
  };
}

export interface TopFavorite {
  contractAddress: string;
  tokenId: string;
  favoritesCount: number;
  isFavorited: boolean;
}

export interface TopFavoritesResponse {
  items: TopFavorite[];
  pagination: {
    offset: number;
    limit: number;
    totalCount: number;
    hasMore: boolean;
  };
}

export interface BatchFavoritesResponse {
  favorites: Record<string, boolean>;
}

/**
 * Add an NFT to favorites
 */
export async function addFavorite(
  contract: string,
  tokenId: string
): Promise<FavoriteResponse> {
  const response = await fetch(favUrl(`/nfts/${contract}/${tokenId}/favorite`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to add favorite' }));
    throw new Error(error.error || 'Failed to add favorite');
  }

  return response.json();
}

/**
 * Remove an NFT from favorites
 */
export async function removeFavorite(
  contract: string,
  tokenId: string
): Promise<FavoriteResponse> {
  const response = await fetch(favUrl(`/nfts/${contract}/${tokenId}/favorite`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to remove favorite' }));
    throw new Error(error.error || 'Failed to remove favorite');
  }

  return response.json();
}

/**
 * Get favorite status for an NFT
 */
export async function getFavoriteStatus(
  contract: string,
  tokenId: string
): Promise<FavoriteStatus> {
  const response = await fetch(favUrl(`/nfts/${contract}/${tokenId}/favorite`), {
    headers: getAuthHeader(),
  });

  if (!response.ok) {
    throw new Error('Failed to get favorite status');
  }

  return response.json();
}

/**
 * Get current user's favorites
 */
export async function getUserFavorites(options: {
  offset?: number;
  limit?: number;
  contract?: string;
} = {}): Promise<UserFavoritesResponse> {
  const params = new URLSearchParams();
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.contract) params.set('contract', options.contract);

  const url = favUrl(`/me/favorites${params.toString() ? '?' + params.toString() : ''}`);
  const response = await fetch(url, {
    headers: getAuthHeader(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required');
    }
    throw new Error('Failed to get favorites');
  }

  return response.json();
}

/**
 * Batch check if multiple NFTs are favorited
 */
export async function checkFavorites(
  nfts: Array<{ contract: string; tokenId: string }>
): Promise<BatchFavoritesResponse> {
  const response = await fetch(favUrl('/me/favorites/check'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ nfts }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      return { favorites: {} };
    }
    throw new Error('Failed to check favorites');
  }

  return response.json();
}

/**
 * Get top favorited NFTs
 */
export async function getTopFavorites(options: {
  offset?: number;
  limit?: number;
  contract?: string;
} = {}): Promise<TopFavoritesResponse> {
  const params = new URLSearchParams();
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.contract) params.set('contract', options.contract);

  const url = favUrl(`/favorites/top${params.toString() ? '?' + params.toString() : ''}`);
  const response = await fetch(url, {
    headers: getAuthHeader(),
  });

  if (!response.ok) {
    throw new Error('Failed to get top favorites');
  }

  return response.json();
}
